// Frames 11 (33:331) and 12 (33:393). One screen, one branch.
//
// THE SCREEN IS ORGANISED AROUND THREE AXES, and that grouping is the product
// decision that replaced `confusion_score`:
//
//   USAGE      — is the glossary being used, and where.
//   ENGAGEMENT — how deeply. Dwell lives here, as its own number.
//   COUPLING   — how concepts relate across the corpus.
//
// The retired score summed `hover_count x dwell` with a 5x-weighted
// abandonment term, so a term ranked high either by being studied carefully or
// by being bounced off — engagement and rejection added together as if they
// were one signal, on a weight nobody ever tuned. Splitting them into named
// groups is what makes each number readable on its own. **Do not add a panel
// here without deciding which axis it belongs to**, and do not build a
// composite that spans two of them.
//
// They are the same drawing down to the stat row; frame 12 replaces the lower
// COUPLING cards with a `State/Empty` panel. The branch is the backend's
// `verdict`, never a threshold re-derived here — a threshold in two places is
// a threshold that disagrees with itself, so `MIN_DOCUMENTS` / `MIN_TERMS` /
// `MIN_SESSIONS` appear nowhere in this tree. The screen renders the counts and
// the reasons the API hands it.
//
// THE SUFFICIENCY VERDICT GATES EVERY RANKED PANEL, ACROSS ALL THREE AXES —
// it is not a coupling-only concern, and grouping the screen did not narrow it.
// `dashboards_terms.py` states the reason on the thresholds themselves: under
// twenty-five observed terms the leaderboard's top row is a coin flip between
// neighbours, and under two hundred observed sessions one visit moves a rank by
// more than the gap between adjacent terms. So the ranked panels (A2's
// leaderboard, document coverage, the divergence map, the impact radius) are
// withheld together and the state is announced ONCE, above the axes, rather
// than leaving three separate holes unexplained.
//
// What survives an insufficient verdict is everything carrying its own
// per-panel measurability guard: A1, A4, B5, C4, B3 and B4 each answer off one
// document, or off corpus text, and each refuses its own rows when it cannot
// speak.
//
// Frame 12 is NOT frame 09. Frame 09 (screens-shell, the overview) is "you have
// no documents". This one is "documents exist, the signal in them is too thin
// to rank", which is why it keeps the stat row and says which count fell short.

import { useNavigate } from "@tanstack/react-router";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  PageHeader,
  Skeleton,
  SkeletonStack,
  StatRow,
  StatTile,
} from "../../ui";
import { useDocTimeline, useVocabularyHealth } from "../../dashboard/api";
import { hrefs } from "../../shell/hrefs";
import {
  useClock,
  useCorpora,
  useCorpusGlossary,
  useCorpusSufficiency,
  useDefinedUsed,
  useFixAndProve,
  useImpactRadius,
  useLearningCurve,
  useLookupTrend,
  usePrereqDebt,
  useSectionDropOff,
  useTermCoupling,
  type ClockCycle,
  type ClockGrain,
} from "./api";
import { AxisNav, type AxisKey } from "./AxisNav";
import { Clock } from "./Clock";
import { CorpusPacking } from "./CorpusPacking";
import { CorpusPicker, resolveCorpus } from "./CorpusPicker";
import { DocumentRidgeline } from "./DocumentRidgeline";
import { VocabularyMatrix } from "./VocabularyMatrix";
import { MostLookedUp } from "./MostLookedUp";
import { DefinedUsed } from "./DefinedUsed";
import { FixAndProve } from "./FixAndProve";
import { LearningCurve } from "./LearningCurve";
import { PrerequisiteDebt } from "./PrerequisiteDebt";
import { SectionDropOff } from "./SectionDropOff";
import { TermCoupling } from "./TermCoupling";
import { DivergenceMap, rankDivergence } from "./DivergenceMap";
import { DocumentCoverage } from "./DocumentCoverage";
import { ImpactRadius } from "./ImpactRadius";
import { RangeChips, type RangeDays } from "./RangeChips";
import { formatCount } from "./format";
import {
  AXIS_COUPLING_BLURB,
  AXIS_COUPLING_TITLE,
  AXIS_ENGAGEMENT_BLURB,
  AXIS_ENGAGEMENT_TITLE,
  AXIS_USAGE_BLURB,
  AXIS_USAGE_TITLE,
} from "./metricLabels";
import "./analytics.css";

/** "1 of 3 documents, 14 of 25 terms and 0 of 200 sessions" — the API's own
 *  strings, joined. Nothing here knows what the numbers mean. */
export function joinReasons(reasons: string[]): string {
  if (reasons.length <= 1) return reasons[0] ?? "";
  return `${reasons.slice(0, -1).join(", ")} and ${reasons[reasons.length - 1]}`;
}

/** One axis group. The heading is the point: it is what tells a reader that the
 *  dwell figure below and the look-up rate above are separate readings rather
 *  than two views of one score. */
function Axis({
  title,
  blurb,
  children,
}: {
  title: string;
  blurb: string;
  children: React.ReactNode;
}) {
  return (
    <section className="maisight-analytics-axis" aria-label={title}>
      <div className="maisight-analytics-axis-head">
        <h2 className="type-labelSm maisight-analytics-muted">{title}</h2>
        <p className="type-bodyS">{blurb}</p>
      </div>
      {children}
    </section>
  );
}

export type AnalyticsScreenProps = {
  orgSlug: string;
  days: RangeDays;
  onRangeChange: (days: RangeDays) => void;
  /** Undefined on the overview, which keeps all three axes. A key renders that
   *  axis alone, plus the full-width views the overview has no room for. */
  axis?: AxisKey;
  /** From `?corpus=`. Falls back to the org's first owned corpus. */
  corpus?: string;
  onCorpusChange?: (corpusId: string) => void;
  /** Clock controls, from `?cycle=` / `?grain=` / `?term=` on the usage axis. */
  clock?: {
    cycle: ClockCycle;
    grain: ClockGrain;
    term?: string;
    onCycleChange: (next: ClockCycle) => void;
    onGrainChange: (next: ClockGrain) => void;
  };
};

export function AnalyticsScreen({
  orgSlug,
  days,
  onRangeChange,
  axis,
  corpus,
  onCorpusChange,
  clock,
}: AnalyticsScreenProps) {
  const navigate = useNavigate();
  const sufficiency = useCorpusSufficiency(orgSlug, days);
  const health = useVocabularyHealth(orgSlug, days);
  const trend = useLookupTrend(orgSlug, days);
  const docs = useDocTimeline(orgSlug, days);
  const ledger = useFixAndProve(orgSlug, days);
  const dropOff = useSectionDropOff(orgSlug, days);
  // THE CORPUS IS RESOLVED, NEVER ASSUMED. This used to pass `orgSlug` as the
  // corpus id on a comment reading "one corpus per org". An org can own
  // several — `acme` owns three and none is named `acme` — so the request
  // resolved to a corpus that does not exist, was still authorized by
  // `_owns_corpus`'s naming-convention fallback, and came back
  // `reconciled: false` on fully reconciled data. `CorpusPicker.tsx` carries
  // the rest of that story.
  const corpora = useCorpora(orgSlug);
  const corpusId = resolveCorpus(corpora.data?.corpora ?? [], corpus);
  // No `?? orgSlug` here. Both hooks stay disabled until the id resolves —
  // falling back to the slug is the assumption this whole block removes, and a
  // brief wrong answer still renders "Pass 2 has never run" over reconciled
  // data on first paint.
  const radius = useImpactRadius(orgSlug, corpusId);
  const debt = usePrereqDebt(orgSlug, corpusId);
  const glossary = useCorpusGlossary(orgSlug, corpusId);
  const coupling = useTermCoupling(orgSlug, days);
  const quadrant = useDefinedUsed(orgSlug, days);
  const learning = useLearningCurve(orgSlug, days);
  const clockReport = useClock(
    orgSlug,
    days,
    clock?.cycle ?? "day",
    clock?.grain ?? "hour",
    clock?.term,
  );

  // The overview shows every axis; an axis route shows its own.
  const shows = (key: AxisKey) => axis === undefined || axis === key;

  const findings = health.data ?? [];
  // B1 ranks on `(1 - confidence) x doc_count`, and the confidence lives on the
  // corpus entity rather than on the health finding — so the two panels share
  // one payload. An absent radius leaves the map ranked on blast radius alone.
  const confidenceByTerm = new Map(
    (radius.data?.rows ?? []).map((row) => [row.term, row.confidence]),
  );
  const divergent = rankDivergence(findings, confidenceByTerm);
  const divergentDocs = new Set(divergent.flatMap((f) => f.doc_ids)).size;
  // The backend's verdict, never a threshold re-derived here. Gates every
  // panel whose output is a RANK across the corpus — see the header comment.
  const ranked = sufficiency.data?.verdict !== "insufficient";

  return (
    <>
      <PageHeader
        eyebrow="Analytics"
        title="Reader analytics"
        description="Three readings of your documentation: whether the glossary is used, how deeply it is read, and how your concepts hang together."
      />
      <AxisNav orgSlug={orgSlug} days={days} active={axis} />
      <div className="maisight-analytics-controls">
        <RangeChips days={days} onChange={onRangeChange} />
        {/* Renders nothing for an org that owns one corpus — a picker with one
         *  option is a question with one answer. */}
        {onCorpusChange ? (
          <CorpusPicker
            corpora={corpora.data?.corpora ?? []}
            value={corpusId}
            onChange={onCorpusChange}
          />
        ) : null}
      </div>

      {sufficiency.isError ? (
        <ErrorState
          headline="Analytics could not be loaded"
          detail="The dashboard could not reach the analytics service."
        />
      ) : sufficiency.isPending || !sufficiency.data ? (
        <Card header="Analytics">
          <SkeletonStack label="Loading analytics">
            <Skeleton shape="block" />
            <Skeleton width={280} />
            <Skeleton width={452} />
          </SkeletonStack>
        </Card>
      ) : (
        <>
          <StatRow>
            <StatTile
              label="Divergent concepts"
              value={health.data === null ? formatCount(null) : formatCount(divergent.length)}
              detail={
                health.data === null
                  ? "Vocabulary health is not enabled for this org."
                  : divergent.length === 0
                    ? "No concept is defined two ways yet."
                    : `across ${divergentDocs} documents`
              }
            />
            <StatTile
              label="Documents"
              value={formatCount(sufficiency.data.documents)}
              detail={`observed in the last ${days} days`}
            />
            <StatTile
              label="Terms tracked"
              value={formatCount(sufficiency.data.terms)}
              detail={`observed in the last ${days} days`}
            />
            <StatTile
              label="Unexplained jargon"
              value={formatCount(sufficiency.data.unexplained_terms)}
              detail={
                sufficiency.data.unexplained_examples.length > 0
                  ? `used once, never defined — e.g. ${sufficiency.data.unexplained_examples.slice(0, 2).join(", ")}`
                  : "used once, never defined"
              }
            />
          </StatRow>

          {/* Said once, above the axes, because it explains an absence in all
           *  three of them. */}
          {ranked ? null : (
            <div className="maisight-analytics-empty-wrap">
              <EmptyState
                headline="Nothing to compare yet"
                body={
                  <>
                    {`Ranking terms against each other, and finding a concept defined more than one way, both need a wider base than this. You have ${joinReasons(sufficiency.data.reasons)}.`}
                    <br />
                    Publish another document and this fills in on the next reconcile.
                  </>
                }
                action={
                  <Button
                    variant="primary"
                    onClick={() => navigate({ to: hrefs(orgSlug).documents })}
                  >
                    Publish another document
                  </Button>
                }
              />
            </div>
          )}

          {/* ---- USAGE ------------------------------------------------------
           *
           * A1 anchors the screen (PRD 16 D1) and leads the axis: it is the one
           * panel that proves an edit changed reader behaviour, and what it
           * proves is a change in look-ups per visit.
           *
           * Every guard is on the ARRAY, never on `data`. The query payload is
           * an `as` cast over untyped JSON, so a 200 carrying some other body
           * is `data`-truthy, typechecks, and then dies inside `.slice` —
           * taking the whole screen with it. `FixAndProve` also reads
           * `editsFound === 0` as "nobody edited anything" and says so;
           * rendering that sentence off a failed fetch would be the same lie by
           * omission the panel exists to avoid. */}
          {shows("usage") ? (
          <Axis title={AXIS_USAGE_TITLE} blurb={AXIS_USAGE_BLURB}>
            {ledger.data?.entries ? (
              <FixAndProve
                entries={ledger.data.entries}
                editsFound={ledger.data.edits_found}
                windowDays={ledger.data.window_days}
              />
            ) : null}
            {ranked && trend.data?.terms ? (
              <MostLookedUp
                orgSlug={orgSlug}
                days={days}
                terms={trend.data.terms}
                termsFound={trend.data.terms_found}
                termsMeasured={trend.data.terms_measured}
              />
            ) : null}
            {dropOff.data?.sections ? <SectionDropOff map={dropOff.data} /> : null}
            {quadrant.data?.terms ? <DefinedUsed report={quadrant.data} /> : null}
            {/* Full-width views, on the axis route only. The overview is
             *  already a long scroll and these need the whole canvas. */}
            {axis === "usage" && clock && clockReport.data ? (
              <Clock
                report={clockReport.data}
                cycle={clock.cycle}
                grain={clock.grain}
                onCycleChange={clock.onCycleChange}
                onGrainChange={clock.onGrainChange}
                termLabel={clock.term}
              />
            ) : null}
            {axis === "usage" ? (
              <DocumentRidgeline timeline={docs.data ?? []} days={days} />
            ) : null}
          </Axis>
          ) : null}

          {/* ---- ENGAGEMENT -------------------------------------------------
           *
           * Dwell-derived readings, reported on their own rather than
           * multiplied into a usage rank. C4 needs one document whose readers
           * re-opened a term, not a corpus wide enough to rank, and carries its
           * own backend-side measurability guard — an UNMEASURED report still
           * has `terms: []`, which is an array and renders. That state is the
           * panel's most important output, not an empty case to hide. */}
          {shows("engagement") ? (
          <Axis title={AXIS_ENGAGEMENT_TITLE} blurb={AXIS_ENGAGEMENT_BLURB}>
            {learning.data?.terms ? <LearningCurve report={learning.data} /> : null}
            {ranked ? <DocumentCoverage timeline={docs.data ?? []} /> : null}
          </Axis>
          ) : null}

          {/* ---- COUPLING ---------------------------------------------------
           *
           * The graph views (PRD 16 §5) — the panels an entrant would have to
           * build term extraction, then cross-doc reconciliation, then
           * term-level behavioural analytics to copy.
           *
           * B3 and B4 survive an insufficient verdict: B3 is computed from
           * corpus text and answers on a corpus far too thin to rank, and B4
           * carries its own traffic floor per pair. */}
          {shows("coupling") ? (
          <Axis title={AXIS_COUPLING_TITLE} blurb={AXIS_COUPLING_BLURB}>
            <div className="maisight-analytics-halves">
              {debt.data?.rows ? <PrerequisiteDebt report={debt.data} /> : null}
              {coupling.data?.pairs ? <TermCoupling report={coupling.data} /> : null}
            </div>
            {/* `corpusId` gates the whole block: DivergenceMap's only use of it
             *  is a link to that corpus's merge queue, and the org slug builds
             *  one that 404s. */}
            {ranked && corpusId ? (
              <>
                <DivergenceMap
                  findings={findings}
                  confidenceByTerm={confidenceByTerm}
                  corpusId={corpusId}
                />
                {radius.data?.rows ? <ImpactRadius report={radius.data} /> : null}
              </>
            ) : null}
            {/* Full-width views, on the axis route only. Both read the corpus
             *  the picker resolved, not the org slug. */}
            {axis === "coupling" && radius.data ? (
              <VocabularyMatrix report={radius.data} />
            ) : null}
            {axis === "coupling" && !glossary.isPending ? (
              <CorpusPacking glossary={glossary.data ?? null} />
            ) : null}
          </Axis>
          ) : null}
        </>
      )}
    </>
  );
}
